import { DateTime } from 'luxon';
import { NutrientTargets, Nutrients, INFOODS } from './data-model/food';
import { Log_Meal, mealSlotKey, pickOneMealPerSlot } from './data-model/log-meal';
import { LogRecordState } from './data-model/user-log';
import { actualMealTargets } from './actual-meal-targets';

/**
 * What ONE meal should contain, given everything else the day has already used up (#5297).
 *
 * This is the question "what should this meal be?", and it is deliberately NOT the question the
 * stored `meal.planningTargets` answers. That field is recomputed from the day total WITH this
 * meal's own food included, so it answers "what should still go into this slot" — correct while
 * the slot is empty, and wrong the moment it is not, because the meal's own calories have been
 * subtracted from the day and the shrunken remainder is then held against the meal itself.
 *
 * Measured on a day of four 500 kcal meals with one of them full and essentially perfect: the
 * meal scores 100% against its own targets, and 88.4% against its `planningTargets`, whose
 * calorie target had collapsed from 500 to 395. The cut-off for "on target" is 95, so a good
 * meal was told its targets could not be matched.
 *
 * For an EMPTY meal the two answers are identical — there is no food to count twice — which is
 * why moving a caller from one to the other cannot change what happens on an empty slot.
 *
 * @param meal      the meal being asked about; it may be full or empty
 * @param dayMeals  every meal of that day, INCLUDING `meal` itself
 * @param dayTargets the day's own targets
 * @param referenceTime any time on that day; it only supplies the time zone the meals' own
 *                      wall-clock times are read in
 */
export function mealTargetsAsIfEmpty(
  meal: Pick<Log_Meal, '_id' | 'name' | 'targets'>,
  dayMeals: Log_Meal[],
  dayTargets: NutrientTargets,
  referenceTime: DateTime,
): NutrientTargets {
  // A day can legitimately hold two documents for one meal slot, and every place that totals a
  // day has to apply the same one-per-slot rule or they disagree with each other (#4728). Sum the
  // survivors only: counting a slot twice would shrink what is left for this meal, which is the
  // very mistake this function exists to avoid.
  const { kept } = pickOneMealPerSlot(dayMeals, referenceTime);

  // Exclude by SLOT rather than by id. If `meal` is itself the document the collapse dropped, its
  // surviving twin holds that slot's food, and excluding only `meal._id` would leave the meal's
  // own food counted against it through the twin.
  const thisSlot = mealSlotKey(meal);

  // what the rest of the day has already used up. this meal's slot is left out — that omission IS
  // the point of this function. a skipped meal is left out too: it was never eaten, so its food
  // must not reduce what the others may contain.
  const dayNutrientsWithoutThisMeal: Nutrients = {};
  for (const other of kept) {
    if (mealSlotKey(other) === thisSlot || other.state === LogRecordState.Skipped) continue;
    for (const tag in other.nutrients) {
      dayNutrientsWithoutThisMeal[tag] = (dayNutrientsWithoutThisMeal[tag] || 0) + other.nutrients[tag];
    }
  }

  // the meals still waiting to be filled. `actualMealTargets` shares out what the day has left
  // between them, so this meal has to be in that pool as well — which is what treating it as
  // empty means. it is added by `actualMealTargets` itself, so it is excluded here.
  const otherEmptyMealTargets = kept
    .filter(other => mealSlotKey(other) !== thisSlot && other.nutrients?.[INFOODS.Calories] == undefined)
    .map(other => other.targets);

  return actualMealTargets(meal.targets, dayNutrientsWithoutThisMeal, dayTargets, otherEmptyMealTargets);
}

/**
 * Pick which targets the meal tuner may actually be handed.
 *
 * Every screen that tunes a meal against a DAY-AWARE figure — `mealTargetsAsIfEmpty` above, or the
 * stored `meal.planningTargets` — has the same problem. On a day whose calories are already spent,
 * that figure comes back carrying no usable calorie target: `actualMealTargets` deletes the
 * calorie tag outright once its min, max and target have all fallen out. `MealTuner.solve` refuses
 * such a figure by throwing, and the screens report a throw as a crash — so an ordinary over-eaten
 * day would be announced to the user, and to Sentry, as a fault. It is not one. Fall back to the
 * meal's own plan figure instead, which does not shrink with the day.
 *
 * @param preferred the day-aware figure the caller would rather tune against. It may be absent
 *                  altogether: `meal-suggestions.ts` reads it out of nav params, and
 *                  `meal-plan.ts` reads the stored `planningTargets`, which a document written by
 *                  an older version of the app need not carry.
 * @param fallback  the meal's own plan targets, used when `preferred` is not usable
 *
 * The threshold MIRRORS the tuner's own refusal rule in `meal-tuner.ts` (`!isFinite(targetEnergy)
 * || targetEnergy < 1`) and must be changed together with it: this function's whole job is to
 * answer "would the tuner refuse this?" before the tuner is called, so the two disagreeing would
 * put the crash back. Note it rejects a NON-FINITE target as well as a small one — the four
 * hand-written copies this replaced all compared with `>` or `>=`, which answer true for
 * `Infinity` and let the tuner throw.
 */
export function tunableTargetsOr(preferred: NutrientTargets | undefined, fallback: NutrientTargets): NutrientTargets {
  const calorieTarget = preferred?.[INFOODS.Calories]?.target;
  return isFinite(calorieTarget) && calorieTarget >= 1 ? preferred : fallback;
}
